import React from 'react';
import '../styles/LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            METRICBRIDGE, LLC ("we," "our," or "us"), operator of Property ROI, is committed to protecting your 
            privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our web application and services.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>We may collect the following personal information:</p>
          <ul>
            <li>Name and email address (for account creation)</li>
            <li>Payment information (processed securely through Stripe)</li>
            <li>Account preferences and settings</li>
            <li>Communication history with our support team</li>
          </ul>

          <h3>2.2 Property and Usage Data</h3>
          <p>We collect information related to your use of our services:</p>
          <ul>
            <li>Property addresses and details you analyze</li>
            <li>ROI calculations and analysis results</li>
            <li>Search history and saved properties</li>
            <li>Usage patterns and feature utilization</li>
            <li>Subscription tier and billing information</li>
          </ul>

          <h3>2.3 Technical Information</h3>
          <p>We automatically collect technical information:</p>
          <ul>
            <li>IP address and location data</li>
            <li>Browser type and version</li>
            <li>Device information and operating system</li>
            <li>Usage analytics and performance metrics</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information for the following purposes:</p>
          <ul>
            <li>Providing and maintaining our services</li>
            <li>Processing property analyses and calculations</li>
            <li>Managing your account and subscription</li>
            <li>Processing payments and billing</li>
            <li>Sending important service notifications</li>
            <li>Improving our services and user experience</li>
            <li>Providing customer support</li>
            <li>Complying with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
          
          <h3>4.1 Service Providers</h3>
          <p>We work with third-party service providers:</p>
          <ul>
            <li>AWS (Amazon Web Services) for hosting and infrastructure</li>
            <li>Stripe for payment processing</li>
            <li>Property data providers for real estate information</li>
            <li>Analytics providers for service improvement</li>
          </ul>

          <h3>4.2 Legal Requirements</h3>
          <p>We may disclose information when required by law or to:</p>
          <ul>
            <li>Comply with legal processes or government requests</li>
            <li>Protect our rights, property, or safety</li>
            <li>Prevent fraud or security threats</li>
            <li>Enforce our Terms of Service</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your 
            personal information against unauthorized access, alteration, disclosure, or destruction. 
            These measures include:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments and updates</li>
            <li>Access controls and authentication measures</li>
            <li>Secure payment processing through PCI-compliant providers</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide our services and comply 
            with legal obligations. Specifically:
          </p>
          <ul>
            <li>Account information: Until account deletion plus 30 days</li>
            <li>Property analysis data: Until account deletion or 2 years of inactivity</li>
            <li>Payment records: As required by applicable tax and financial regulations</li>
            <li>Usage logs: Up to 12 months for service improvement purposes</li>
          </ul>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
            <li><strong>Objection:</strong> Object to certain processing activities</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing</li>
          </ul>
          <p>To exercise these rights, please contact us at privacy@metricbridge.com</p>
        </section>

        <section>
          <h2>8. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar technologies to enhance your experience, analyze usage, 
            and provide personalized content. You can control cookie preferences through your 
            browser settings, though some features may not function properly if cookies are disabled.
          </p>
        </section>

        <section>
          <h2>9. Third-Party Links</h2>
          <p>
            Our service may contain links to third-party websites. We are not responsible for the 
            privacy practices of these external sites. We encourage you to review the privacy 
            policies of any third-party sites you visit.
          </p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under 18 years of age. We do not knowingly 
            collect personal information from children. If we become aware that a child has provided 
            personal information, we will take steps to delete such information.
          </p>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own. 
            We ensure appropriate safeguards are in place to protect your information in accordance 
            with applicable data protection laws.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices or 
            applicable laws. We will notify you of material changes by posting the updated policy 
            on our website and updating the "Last updated" date above.
          </p>
        </section>

        <section>
          <h2>13. Contact Information</h2>
          <p>
            If you have questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <p>
            Email: privacy@metricbridge.com<br/>
            Support: support@metricbridge.com<br/>
            Address: [METRICBRIDGE, LLC Business Address]
          </p>
        </section>

        <section>
          <h2>14. Data Protection Officer</h2>
          <p>
            If you are located in the European Union and have concerns about our data processing, 
            you may contact our Data Protection Officer at dpo@metricbridge.com or lodge a complaint 
            with your local supervisory authority.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;